import type { ObjectWorkspaceWorkbenchContext } from '@heidi/agents';
import { closeArtifactAtom, openArtifactAtom } from '@heidi/agents/data';
import { useStore } from 'jotai';
import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceChatHarnessMessages as messages } from './object-workspace-chat-harness.messages';
import {
  emptyObjectWorkspaceChatTab,
  isObjectWorkspaceChatTabProtected,
  normalizedObjectWorkspaceTabTitle,
  type ObjectWorkspaceChatAnnounce,
  type ObjectWorkspaceChatHarnessState,
} from './object-workspace-chat-utils';
import type { ObjectWorkspaceChatTab } from './object-workspace-project-chat-tab';

interface UseObjectWorkspacePromptTabsArgs {
  object: ObjectWorkspaceWorkbenchContext['object'];
  anchorSelection: ObjectWorkspaceWorkbenchContext['anchorSelection'];
  workSelection: ObjectWorkspaceWorkbenchContext['workSelection'];
  newTabTitle: string;
  announce: ObjectWorkspaceChatAnnounce;
  setState: Dispatch<SetStateAction<ObjectWorkspaceChatHarnessState>>;
}

export function useObjectWorkspacePromptTabs({
  object,
  anchorSelection,
  workSelection,
  newTabTitle,
  announce,
  setState,
}: UseObjectWorkspacePromptTabsArgs): { addEmptyTab: () => void } {
  const intl = useIntl();
  const store = useStore();
  const nextTabNumber = useRef(1);
  const handledAnchorSequence = useRef<number>();
  const handledWorkSequence = useRef<number>();

  const addEmptyTab = useCallback(() => {
    const number = ++nextTabNumber.current;
    const id = `${object.kind}-${object.id}-work-${number}`;
    setState((current) =>
      announce(
        current,
        {
          activeTabId: id,
          tabs: [...current.tabs, emptyObjectWorkspaceChatTab(id, newTabTitle)],
        },
        intl.formatMessage(messages.emptyTabAdded, { number })
      )
    );
  }, [announce, intl, newTabTitle, object.id, object.kind, setState]);

  const startPrompt = useCallback(
    (
      prompt: string,
      source: { workItemId?: string; anchorKey?: string; sessionId?: string },
      autoSubmit: boolean
    ) => {
      const newTabId = `${object.kind}-${object.id}-work-${++nextTabNumber.current}`;
      setState((current) => {
        const existing = current.tabs.find(
          (tab) =>
            (source.workItemId && tab.workItemId === source.workItemId) ||
            (source.anchorKey && tab.anchorKey === source.anchorKey)
        );
        if (existing) {
          return announce(
            current,
            { ...current, activeTabId: existing.id },
            intl.formatMessage(messages.tabSelected, { title: existing.title })
          );
        }

        const active = current.tabs.find((tab) => tab.id === current.activeTabId);
        const canReuse =
          active &&
          active.kind !== 'heidi' &&
          !active.projectId &&
          !isObjectWorkspaceChatTabProtected(active);
        const targetId = canReuse ? active.id : newTabId;
        const target: ObjectWorkspaceChatTab = {
          ...(canReuse ? active : emptyObjectWorkspaceChatTab(targetId, newTabTitle)),
          id: targetId,
          kind: 'work',
          title: normalizedObjectWorkspaceTabTitle(prompt, newTabTitle),
          prefilledPrompt: autoSubmit ? undefined : prompt,
          autoSubmitPrompt: autoSubmit ? prompt : undefined,
          isDraftEmpty: autoSubmit,
          workItemId: source.workItemId,
          anchorKey: source.anchorKey,
          sessionId: source.sessionId,
        };
        return announce(
          current,
          {
            activeTabId: targetId,
            tabs: canReuse
              ? current.tabs.map((tab) => (tab.id === targetId ? target : tab))
              : [...current.tabs, target],
          },
          intl.formatMessage(canReuse ? messages.tabSelected : messages.tabAdded, {
            title: target.title,
          })
        );
      });
    },
    [announce, intl, newTabTitle, object.id, object.kind, setState]
  );

  useEffect(() => {
    if (!workSelection || handledWorkSequence.current === workSelection.sequence) return;
    handledWorkSequence.current = workSelection.sequence;
    startPrompt(workSelection.work.title, { workItemId: workSelection.work.id }, true);
  }, [startPrompt, workSelection]);
  useEffect(() => {
    if (!anchorSelection || handledAnchorSequence.current === anchorSelection.sequence)
      return;
    handledAnchorSequence.current = anchorSelection.sequence;
    const { anchor } = anchorSelection;
    if (anchor.kind === 'session') {
      // The canvas grounds the turn and is carried into the new chat, so it must not name another subject.
      const open = store.get(openArtifactAtom);
      if (open && !('sessionId' in open && open.sessionId === anchor.id)) {
        store.set(closeArtifactAtom);
      }
    }
    startPrompt(
      anchorSelection.prompt,
      {
        anchorKey: `${anchor.kind}:${anchor.id}`,
        sessionId: anchor.kind === 'session' ? anchor.id : undefined,
      },
      false
    );
  }, [anchorSelection, startPrompt, store]);

  return { addEmptyTab };
}
